import React from "react";
import { Alert } from "react-bootstrap";

import styles from "./style.module.css";

const OptimizeWarnings = (props) => {
  const { setup, isContinuous } = props;
  if (!setup || isContinuous) {
    return null;
  }

  const { falsePositiveLabel, falseNegativeLabel, truePositiveLabel, trueNegativeLabel, trueAction, falseAction } =
    setup;

  const checkIfActionExists = (type) => {
    let returnMessage = type ? "Pursue" : "Ignore";

    if (type && trueAction) {
      returnMessage = trueAction;
    } else if (falseAction) {
      returnMessage = falseAction;
    }

    return returnMessage;
  };

  const selectedOptions = {
    truePositive: Number(setup.conversionGain),
    falsePositive: Number(setup.conversionCost),
    falseNegative: Number(setup.conversionMissed),
    trueNegative: Number(setup.conversionSaved)
  };

  const { truePositive, falsePositive, falseNegative, trueNegative } = selectedOptions;

  const renderMessage = () => {
    if (truePositive > falseNegative && falsePositive > trueNegative) {
      return (
        <React.Fragment>
          <p>
            The impact for {truePositiveLabel} is greater than {falseNegativeLabel} and {falsePositiveLabel} is greater
            than {trueNegativeLabel}, so you are always better off when you {checkIfActionExists(true)} every time.
          </p>
          <p>
            Typically correct predictions have greater value than the corresponding incorrect prediction, if the impact
            of {falsePositiveLabel} is actually greater than {trueNegativeLabel}, you do not need a prediction model for
            this project and should always {checkIfActionExists(true)}.
          </p>
        </React.Fragment>
      );
    } else if (truePositive < falseNegative && falsePositive < trueNegative) {
      return (
        <React.Fragment>
          <p>
            The impact for {falseNegativeLabel} is greater than {truePositiveLabel} and {trueNegativeLabel} is greater
            than {falsePositiveLabel}, so you are always better off when you always {checkIfActionExists(false)}.
          </p>
          <p>
            Typically correct predictions have greater value than the corresponding incorrect predictions, if the impact
            of {falseNegativeLabel} is actually greater than {truePositiveLabel}, you do not need a prediction model for
            this project and should always {checkIfActionExists(false)}.
          </p>
        </React.Fragment>
      );
    } else if (truePositive === falseNegative && falsePositive === trueNegative) {
      return (
        <React.Fragment>
          <p>
            The impact for {truePositiveLabel} is equal to {falseNegativeLabel} and {falsePositiveLabel} is equal to{" "}
            {trueNegativeLabel}, so it doesn’t matter if you {checkIfActionExists(true)} or {checkIfActionExists(false)}
            .
          </p>
          <p>
            Typically correct predictions have greater value than the corresponding incorrect predictions, if{" "}
            {truePositiveLabel} is actually equal to {falseNegativeLabel} and {falsePositiveLabel} is actually equal to{" "}
            {trueNegativeLabel}, you do not need a prediction model for this project because the payoff is the same if
            you {checkIfActionExists(true)} or {checkIfActionExists(false)}.
          </p>
        </React.Fragment>
      );
    } else if (truePositive > falseNegative && falsePositive === trueNegative) {
      return (
        <React.Fragment>
          <p>
            The impact for {truePositiveLabel} is greater than {falseNegativeLabel} and {falsePositiveLabel} is equal to{" "}
            {trueNegativeLabel}, so you always receive greater or equal impact when you {checkIfActionExists(true)}{" "}
            every time.
          </p>
          <p>
            Typically correct predictions have greater value than the corresponding incorrect prediction, if the impact
            of {falsePositiveLabel} is actually equal to {trueNegativeLabel}, a prediction model will provide minimal
            value for this project as the AI will only tell you to {checkIfActionExists(false)} when it is virtually
            certain of the outcome, and you are guaranteed to receive the maximum payoff if you always{" "}
            {checkIfActionExists(true)}.
          </p>
        </React.Fragment>
      );
    } else if (truePositive < falseNegative && falsePositive === trueNegative) {
      return (
        <React.Fragment>
          <p>
            The impact for {falseNegativeLabel} is greater than {truePositiveLabel} and {trueNegativeLabel} is equal to{" "}
            {falsePositiveLabel}, so you always receive equal or greater impact when you {checkIfActionExists(false)}{" "}
            every time.
          </p>
          <p>
            Typically correct predictions have greater value than the corresponding incorrect prediction, if the impact
            of {falseNegativeLabel} is actually greater than {truePositiveLabel}, and {trueNegativeLabel} is equal to{" "}
            {falseNegativeLabel}, a prediction model will provide minimal value for this project as the AI will only
            tell you to {checkIfActionExists(true)} when it is virtually certain of the outcome, and you are guaranteed
            to receive the maximum payoff if you always {checkIfActionExists(false)}.
          </p>
        </React.Fragment>
      );
    } else if (truePositive === falseNegative && falsePositive < trueNegative) {
      return (
        <React.Fragment>
          <p>
            The impact for {falseNegativeLabel} is equal to {truePositiveLabel} and {trueNegativeLabel} is greater than{" "}
            {falsePositiveLabel}, so you always receive equal or greater impact when you {checkIfActionExists(false)}{" "}
            every time.
          </p>
          <p>
            Typically correct predictions have greater value than the corresponding incorrect prediction, if the impact
            of {falseNegativeLabel} is actually equal to {truePositiveLabel}, a prediction model will provide minimal
            value for this project as the AI will only tell you to {checkIfActionExists(true)} when it is virtually
            certain of the outcome, and you are guaranteed to receive the maximum payoff if you always{" "}
            {checkIfActionExists(false)}.
          </p>
        </React.Fragment>
      );
    } else if (truePositive === falseNegative && falsePositive > trueNegative) {
      return (
        <React.Fragment>
          <p>
            The impact for {falseNegativeLabel} is equal to {truePositiveLabel} and {trueNegativeLabel} is less than{" "}
            {falsePositiveLabel}, so you always receive equal or greater impact when you {checkIfActionExists(true)}{" "}
            every time.
          </p>
          <p>
            Typically correct predictions have greater value than the corresponding incorrect prediction, if the impact
            of {falseNegativeLabel} is actually equal to {truePositiveLabel} and the impact of {falsePositiveLabel} is
            actually greater than {trueNegativeLabel}, a prediction model will provide minimal value for this project as
            the AI will only tell you to {checkIfActionExists(false)} when it is virtually certain of the outcome, and
            you are guaranteed to receive the maximum payoff if you always {checkIfActionExists(true)}.
          </p>
        </React.Fragment>
      );
    }

    return null;
  };

  const warningMessageToDisplay = renderMessage();

  return (
    <React.Fragment>
      {warningMessageToDisplay ? (
        <div className={styles.assumptionEditItem} style={{ marginTop: "6px" }}>
          <Alert style={{ width: "95%" }} bsStyle="danger">
            {warningMessageToDisplay}
          </Alert>
        </div>
      ) : null}
    </React.Fragment>
  );
};

export default OptimizeWarnings;
